import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/features/authentication/presentation/stores/auth.store'

import DefaultLayout from '@/shared/layouts/DefaultLayout.vue'
import AuthLayout from '@/shared/layouts/AuthLayout.vue'

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/dashboard',
  },
  {
    path: '/login',
    component: AuthLayout,
    children: [
      {
        path: '',
        name: 'login',
        component: () => import('@/features/authentication/presentation/pages/LoginPage.vue'),
        meta: {
          requiresAuth: false,
          title: 'Login',
        },
      },
    ],
  },
  {
    path: '/',
    component: DefaultLayout,
    meta: { requiresAuth: true },
    children: [
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/features/dashboard/presentation/pages/DashboardPage.vue'),
        meta: {
          title: 'Dashboard',
        },
      },
      {
        path: 'users',
        name: 'users-list',
        component: () => import('@/features/users/presentation/pages/UsersListPage.vue'),
        meta: {
          title: 'Users',
        },
      },
      {
        path: 'users/:id',
        name: 'user-detail',
        component: () => import('@/features/users/presentation/pages/UserDetailPage.vue'),
        meta: {
          title: 'User Details',
        },
      },
      {
        path: 'tasks',
        name: 'tasks-list',
        component: () => import('@/features/tasks/presentation/pages/TasksListPage.vue'),
        meta: {
          title: 'Tasks',
        },
      },
      {
        path: 'subscriptions',
        name: 'subscriptions',
        component: () => import('@/features/subscriptions/presentation/pages/SubscriptionsPage.vue'),
        meta: {
          requiresAuth: true,
          title: 'Subscriptions',
        },
      },
      {
        path: 'transactions',
        name: 'transactions',
        component: () => import('@/features/transactions/presentation/pages/TransactionsPage.vue'),
        meta: {
          requiresAuth: true,
          title: 'Transactions',
        },
      },
      {
        path: 'analytics',
        name: 'analytics',
        component: () => import('@/features/analytics/presentation/pages/AnalyticsPage.vue'),
        meta: {
          title: 'Analytics',
        },
      },
      {
        path: 'focus-sessions',
        name: 'focus-sessions',
        component: () => import('@/features/focus-sessions/presentation/pages/FocusSessionsPage.vue'),
        meta: {
          title: 'Focus Sessions',
        },
      },
      {
        path: 'gamification',
        name: 'gamification',
        component: () => import('@/features/gamification/presentation/pages/GamificationPage.vue'),
        meta: {
          title: 'Gamification',
        },
      },
      {
        path: 'notifications',
        name: 'notifications',
        component: () => import('@/features/notifications/presentation/pages/NotificationsPage.vue'),
        meta: {
          title: 'Notifications',
        },
      },
      {
        path: 'audit-log',
        name: 'audit-log',
        component: () => import('@/features/audit-log/presentation/pages/AuditLogPage.vue'),
        meta: {
          title: 'Audit Log',
        },
      },
      {
        path: 'ai-keys',
        name: 'ai-keys',
        component: () => import('@/features/ai-keys/presentation/pages/AIKeysPage.vue'),
        meta: {
          title: 'AI API Keys',
        },
      },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/features/settings/presentation/pages/SettingsPage.vue'),
        meta: {
          title: 'Settings',
        },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/shared/components/NotFound.vue'),
    meta: {
      title: 'Page Not Found',
    },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    return { top: 0 }
  },
})

router.beforeEach((to, _from, next) => {
  const authStore = useAuthStore()

  // Initialize auth state from storage if not already done
  if (!authStore.isAuthenticated) {
    authStore.initializeAuth()
  }

  // Check if route requires authentication
  // A route requires auth if ANY matched record has requiresAuth: true
  const requiresAuth = to.matched.some((record) => record.meta.requiresAuth === true)

  if (requiresAuth && !authStore.isAuthenticated) {
    // Redirect to login with the intended destination
    next({
      name: 'login',
      query: { redirect: to.fullPath },
    })
  } else if (to.name === 'login' && authStore.isAuthenticated) {
    // Redirect authenticated users away from login page
    next({ name: 'dashboard' })
  } else {
    next()
  }
})

router.afterEach((to) => {
  // Update document title
  const title = to.meta.title as string
  document.title = title ? `${title} | Bajar Admin` : 'Bajar Admin'
})

export default router
